import { PIGMENT_PUBLICATION_ENVELOPE_FILE, canonicalPigmentJson, canonicalPigmentJsonBytes, isPigmentPublicationPath, sha256PigmentBytes, verifyPigmentPublicationEnvelope, } from "./publication.js";
/** Canonical transport around the existing publication envelope and inventory. */
export const PIGMENT_PUBLICATION_ARCHIVE_FORMAT = "pigment-publication-root-tar+gzip.v1";
export const PIGMENT_PUBLICATION_ARCHIVE_MEDIA_TYPE = "application/gzip";
const UTF8 = new TextEncoder();
const TAR_BLOCK_BYTES = 512;
const MAX_ENTRIES = 100_000;
const MAX_FILE_BYTES = 536_870_912;
const MAX_TOTAL_BYTES = 4_294_967_296;
function fail(message) {
    throw new TypeError(`Pigment publication archive refused: ${message}`);
}
function exactKeys(value, allowed) {
    return typeof value === "object" && value !== null && !Array.isArray(value)
        && Object.keys(value).length === allowed.length
        && Object.keys(value).every((key) => allowed.includes(key));
}
function cloneFile(value, label) {
    if (!exactKeys(value, ["path", "mediaType", "bytes", "type"]))
        fail(`${label} has an unknown or missing field.`);
    if (value.type !== "file")
        fail(`${label} must be a regular byte file; symlinks and special files are forbidden.`);
    if (!isPigmentPublicationPath(value.path))
        fail(`${label} path is not a safe canonical publication path.`);
    if (typeof value.mediaType !== "string" || !value.mediaType.trim())
        fail(`${label} media type is invalid.`);
    if (!(value.bytes instanceof Uint8Array))
        fail(`${label} bytes must be a Uint8Array.`);
    if (value.bytes.byteLength > MAX_FILE_BYTES)
        fail(`${label} exceeds the per-file archive limit.`);
    return { path: value.path, mediaType: value.mediaType, type: "file", bytes: Uint8Array.from(value.bytes) };
}
function compareUtf8(left, right) {
    const a = UTF8.encode(left);
    const b = UTF8.encode(right);
    const length = Math.min(a.length, b.length);
    for (let index = 0; index < length; index += 1) {
        if (a[index] !== b[index])
            return a[index] - b[index];
    }
    return a.length - b.length;
}
function equalBytes(left, right) {
    return left.byteLength === right.byteLength && left.every((byte, index) => byte === right[index]);
}
function tarPathParts(filePath) {
    const encoded = UTF8.encode(filePath);
    if (encoded.byteLength <= 100)
        return { name: encoded, prefix: new Uint8Array() };
    const slashes = [...filePath.matchAll(/\//gu)].map((match) => match.index).reverse();
    for (const slash of slashes) {
        const prefix = UTF8.encode(filePath.slice(0, slash));
        const name = UTF8.encode(filePath.slice(slash + 1));
        if (prefix.byteLength <= 155 && name.byteLength > 0 && name.byteLength <= 100)
            return { name, prefix };
    }
    return fail(`path ${filePath} cannot be represented canonically in POSIX ustar.`);
}
function writeBytes(target, offset, bytes, width) {
    if (bytes.byteLength > width)
        fail("internal ustar field overflow.");
    target.set(bytes, offset);
}
function writeOctal(target, offset, width, value) {
    const octal = value.toString(8);
    if (octal.length > width - 1)
        fail("internal ustar numeric field overflow.");
    const text = `${"0".repeat(width - 1 - octal.length)}${octal}\0`;
    writeBytes(target, offset, UTF8.encode(text), width);
}
function tarHeader(file) {
    const header = new Uint8Array(TAR_BLOCK_BYTES);
    const { name, prefix } = tarPathParts(file.path);
    writeBytes(header, 0, name, 100);
    writeOctal(header, 100, 8, 0o644);
    writeOctal(header, 108, 8, 0);
    writeOctal(header, 116, 8, 0);
    writeOctal(header, 124, 12, file.bytes.byteLength);
    writeOctal(header, 136, 12, 0);
    header.fill(0x20, 148, 156);
    header[156] = 0x30; // POSIX regular file.
    writeBytes(header, 257, UTF8.encode("ustar\0"), 6);
    writeBytes(header, 263, UTF8.encode("00"), 2);
    writeBytes(header, 345, prefix, 155);
    const checksum = header.reduce((sum, byte) => sum + byte, 0);
    const checksumText = `${"0".repeat(6 - checksum.toString(8).length)}${checksum.toString(8)}\0 `;
    writeBytes(header, 148, UTF8.encode(checksumText), 8);
    return header;
}
function concatenate(chunks, totalBytes) {
    if (!Number.isSafeInteger(totalBytes) || totalBytes < 0 || totalBytes > MAX_TOTAL_BYTES + 16 * 1024 * 1024) {
        return fail("archive byte length exceeds the portable limit.");
    }
    const output = new Uint8Array(totalBytes);
    let offset = 0;
    for (const chunk of chunks) {
        output.set(chunk, offset);
        offset += chunk.byteLength;
    }
    return output;
}
function createTar(files) {
    const chunks = [];
    let totalBytes = 2 * TAR_BLOCK_BYTES;
    for (const file of files) {
        const padding = (TAR_BLOCK_BYTES - (file.bytes.byteLength % TAR_BLOCK_BYTES)) % TAR_BLOCK_BYTES;
        chunks.push(tarHeader(file), file.bytes);
        if (padding)
            chunks.push(new Uint8Array(padding));
        totalBytes += TAR_BLOCK_BYTES + file.bytes.byteLength + padding;
    }
    chunks.push(new Uint8Array(2 * TAR_BLOCK_BYTES));
    return concatenate(chunks, totalBytes);
}
function crc32(bytes) {
    let crc = 0xffffffff;
    for (const byte of bytes) {
        crc ^= byte;
        for (let bit = 0; bit < 8; bit += 1)
            crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
    }
    return (crc ^ 0xffffffff) >>> 0;
}
function littleEndian32(value) {
    return Uint8Array.of(value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff);
}
/** A fixed-header gzip stream containing only canonical stored DEFLATE blocks. */
function gzipStored(bytes) {
    const chunks = [Uint8Array.of(0x1f, 0x8b, 0x08, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0xff)];
    let totalBytes = 18;
    for (let offset = 0; offset < bytes.byteLength;) {
        const length = Math.min(65_535, bytes.byteLength - offset);
        const final = offset + length === bytes.byteLength;
        const inverse = (~length) & 0xffff;
        chunks.push(Uint8Array.of(final ? 0x01 : 0x00, length & 0xff, (length >>> 8) & 0xff, inverse & 0xff, (inverse >>> 8) & 0xff));
        chunks.push(bytes.subarray(offset, offset + length));
        totalBytes += 5 + length;
        offset += length;
    }
    chunks.push(littleEndian32(crc32(bytes)), littleEndian32(bytes.byteLength >>> 0));
    return concatenate(chunks, totalBytes);
}
/**
 * Wrap one already-canonical publication envelope and its exact closed output
 * inventory in deterministic root-level POSIX ustar + fixed-header gzip bytes.
 * No second manifest is introduced: extraction yields only
 * `pigment-publication.json` and the envelope-declared output files.
 */
export async function createPigmentPublicationArchive(input) {
    if (!exactKeys(input, ["envelope", "envelopeFile", "outputFiles"]))
        fail("input has an unknown or missing field.");
    const envelope = JSON.parse(canonicalPigmentJson(input.envelope));
    const envelopeFile = cloneFile(input.envelopeFile, "envelopeFile");
    if (envelopeFile.path !== PIGMENT_PUBLICATION_ENVELOPE_FILE || envelopeFile.mediaType !== "application/json") {
        fail(`envelopeFile must be ${PIGMENT_PUBLICATION_ENVELOPE_FILE} with application/json media type.`);
    }
    const expectedEnvelopeBytes = canonicalPigmentJsonBytes(envelope);
    if (!equalBytes(envelopeFile.bytes, expectedEnvelopeBytes))
        fail("envelope bytes must equal the exact canonical existing envelope.");
    if (!Array.isArray(input.outputFiles) || input.outputFiles.length + 1 > MAX_ENTRIES)
        fail("output file count exceeds the archive limit.");
    const outputFiles = input.outputFiles.map((file, index) => cloneFile(file, `outputFiles[${index}]`));
    const allFiles = [envelopeFile, ...outputFiles];
    const paths = new Set();
    let totalInputBytes = 0;
    for (const file of allFiles) {
        if (paths.has(file.path))
            fail(`duplicate archive path ${file.path}.`);
        paths.add(file.path);
        totalInputBytes += file.bytes.byteLength;
        if (totalInputBytes > MAX_TOTAL_BYTES)
            fail("publication bytes exceed the total archive limit.");
        tarPathParts(file.path);
    }
    const actualFiles = await Promise.all(outputFiles.map(async (file) => ({
        path: file.path,
        mediaType: file.mediaType,
        sizeBytes: file.bytes.byteLength,
        sha256: await sha256PigmentBytes(file.bytes),
        type: "file",
    })));
    const validation = await verifyPigmentPublicationEnvelope(envelope, actualFiles);
    if (!validation.ok)
        fail(`envelope or inventory is invalid: ${validation.issues[0]?.path ?? "$"} ${validation.issues[0]?.message ?? "validation failed"}`);
    allFiles.sort((left, right) => compareUtf8(left.path, right.path));
    const bytes = gzipStored(createTar(allFiles));
    return {
        format: PIGMENT_PUBLICATION_ARCHIVE_FORMAT,
        mediaType: PIGMENT_PUBLICATION_ARCHIVE_MEDIA_TYPE,
        bytes,
        sha256: await sha256PigmentBytes(bytes),
        sizeBytes: bytes.byteLength,
        entries: allFiles.map((file) => file.path),
    };
}
